'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { useChat } from '@ai-sdk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport } from 'ai';
import type { ChatMessage } from '@/lib/types';
import { generateUUID } from '@/lib/utils';
import { useAuth } from '@/shared/providers/AuthProvider';
import { useI18n } from '@/shared/providers/I18nProvider';
import {
  getConversations,
  getConversation,
  createConversation,
  deleteConversation,
  saveConversationGreeting,
  type ConversationItem,
  type LatestConversation,
} from '@/lib/conversation-api';
import { PreviewMessage } from './message/index';
import { ThemeToggle } from '@/shared/ui/ThemeToggle';
import { ChatHeader } from 'widgets/chat/ui/ChatHeader';
import { ChatInput } from 'widgets/chat/ui/ChatInput';
import { ChatDescription } from 'widgets/chat/ui/chat-shell/ChatDescription';
import { TopicSelector } from 'widgets/chat/ui/TopicSelector';
import { TypingIndicator } from 'widgets/chat/ui/TypingIndicator';
import { ChatErrorAlert } from 'widgets/chat/ui/chat-shell/ChatErrorAlert';
import { ConversationDrawer } from 'widgets/chat/ui/ConversationDrawer';

const PENDING_MSG_KEY = 'kdoc-pending-message';

function getUserId(token: string): string | null {
  try {
    return (JSON.parse(atob(token.split('.')[1])) as { sub: string }).sub;
  } catch {
    return null;
  }
}

type ChatProps = {
  api?: string;
  conversationId?: string;
};

// ─── 데이터 레이어 ────────────────────────────────────────────────
export function Chat({ api = '/api/chat', conversationId }: ChatProps) {
  const { accessToken } = useAuth();
  const { lang } = useI18n();
  const router = useRouter();
  const queryClient = useQueryClient();
  const userId = accessToken ? getUserId(accessToken) : null;

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [isTopicLoading, setIsTopicLoading] = React.useState(false);
  // 기존 대화에 greeting 저장 후 ChatUI 강제 리마운트용 카운터
  const [chatKey, setChatKey] = React.useState(0);

  // 대화 목록
  const { data: conversations = [] } = useQuery<ConversationItem[]>({
    queryKey: ['conversations', userId],
    queryFn: () => getConversations(accessToken!),
    enabled: !!userId,
    staleTime: Infinity,
  });

  // 선택된 대화의 메시지 로드
  const { data: conversation } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => getConversation(conversationId!, accessToken!),
    enabled: !!conversationId && !!userId,
    staleTime: Infinity,
  });

  const initialMessages = React.useMemo(
    () =>
      (conversation?.messages ?? []).map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        parts: m.parts,
        metadata: {},
      })) as ChatMessage[],
    [conversation],
  );

  // conversationId가 있으면 대화 데이터가 로드될 때까지 ChatUI 렌더링 대기
  const isReady = !conversationId || conversation !== undefined;

  function onSelectConversation(id: string) {
    router.push(`/${lang}/chat/${id}`);
    setIsDrawerOpen(false);
  }

  async function onNewChat() {
    if (!accessToken) return;
    const { conversationId: newId } = await createConversation(accessToken);
    await queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
    router.push(`/${lang}/chat/${newId}`);
    setIsDrawerOpen(false);
  }

  async function onDeleteConversation(id: string) {
    if (!accessToken) return;
    try {
      await deleteConversation(id, accessToken);
      await queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
      if (id === conversationId) {
        router.push(`/${lang}/chat`);
      }
    } catch {
      // 삭제 실패 시 무시
    }
  }

  // /chat 에서 주제 선택: 대화 생성 → greeting 저장 → 이동
  async function onSelectTopicNew(topic: 'ESTIMATE' | 'REVIEW') {
    if (!accessToken) return;
    setIsTopicLoading(true);
    try {
      const { conversationId: newId } = await createConversation(accessToken);
      const greeting = await saveConversationGreeting(newId, accessToken, topic);
      await queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
      queryClient.setQueryData(['conversation', newId], {
        conversationId: newId,
        messages: [{ id: greeting.id, role: greeting.role, parts: [{ type: 'text', text: greeting.content }] }],
      } satisfies LatestConversation);
      router.push(`/${lang}/chat/${newId}`);
    } finally {
      setIsTopicLoading(false);
    }
  }

  // /chat/:id 에서 주제 선택: greeting 저장 → 캐시 업데이트 → ChatUI 리마운트
  async function onSelectTopicExisting(topic: 'ESTIMATE' | 'REVIEW') {
    if (!accessToken || !conversationId) return;
    setIsTopicLoading(true);
    try {
      const greeting = await saveConversationGreeting(conversationId, accessToken, topic);
      queryClient.setQueryData(['conversation', conversationId], (old: LatestConversation | undefined) => ({
        conversationId: conversationId,
        messages: [
          ...(old?.messages ?? []),
          { id: greeting.id, role: greeting.role, parts: [{ type: 'text', text: greeting.content }] },
        ],
      } satisfies LatestConversation));
      setChatKey((k) => k + 1);
    } finally {
      setIsTopicLoading(false);
    }
  }

  const onSelectTopic = userId ? (conversationId ? onSelectTopicExisting : onSelectTopicNew) : undefined;

  return (
    <>
      {userId && (
        <ConversationDrawer
          open={isDrawerOpen}
          conversations={conversations}
          selectedId={conversationId ?? null}
          onSelect={onSelectConversation}
          onDelete={onDeleteConversation}
          onNewChat={onNewChat}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
      {isReady ? (
        <ChatUI
          key={`${conversationId ?? 'new'}-${chatKey}`}
          api={api}
          accessToken={accessToken}
          conversationId={conversationId ?? null}
          initialMessages={initialMessages}
          onSelectTopic={onSelectTopic}
          isTopicLoading={isTopicLoading}
          onOpenDrawer={userId ? () => setIsDrawerOpen(true) : undefined}
        />
      ) : (
        <div className='bg-background flex h-screen w-full items-center justify-center'>
          <div className='text-muted-foreground animate-pulse text-sm'>대화를 불러오는 중...</div>
        </div>
      )}
    </>
  );
}

// ─── UI 레이어 ────────────────────────────────────────────────────
type ChatUIProps = {
  api: string;
  accessToken: string | null;
  conversationId: string | null;
  initialMessages: ChatMessage[];
  onSelectTopic?: (topic: 'ESTIMATE' | 'REVIEW') => Promise<void>;
  isTopicLoading?: boolean;
  onOpenDrawer?: () => void;
};

function ChatUI({ api, accessToken, conversationId, initialMessages, onSelectTopic, isTopicLoading, onOpenDrawer }: ChatUIProps) {
  const transport = React.useMemo(
    () =>
      new DefaultChatTransport({
        api,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        prepareSendMessagesRequest({ messages }) {
          if (accessToken) {
            // 로그인 상태: 마지막 메시지만 전송, 서버가 DB에서 이력 조회
            return {
              body: {
                message: messages[messages.length - 1],
                ...(conversationId ? { conversationId } : {}),
              },
            };
          } else {
            // 비로그인 상태: 전체 메시지 전송, 서버가 그대로 LLM에 전달
            return { body: { messages } };
          }
        },
      }),
    [api, accessToken, conversationId],
  );

  const { messages, sendMessage, status, error, stop, setMessages } = useChat<ChatMessage>({
    transport,
    generateId: generateUUID,
  });

  // ChatUI는 key로 리마운트되므로 이 effect는 항상 한 번만 실행됨
  React.useEffect(() => {
    if (initialMessages.length > 0) {
      setMessages(initialMessages);
    }

    // 이전 페이지에서 저장된 pending message 자동 전송
    const pending = sessionStorage.getItem(PENDING_MSG_KEY);
    if (pending && conversationId) {
      sessionStorage.removeItem(PENDING_MSG_KEY);
      void sendMessage({ parts: [{ type: 'text', text: pending }] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [input, setInput] = React.useState('');
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const isBusy = status === 'streaming' || status === 'submitted';

  React.useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length, status]);

  async function onSend() {
    const text = input.trim();
    if (!text || isBusy) return;
    setInput('');
    await sendMessage({ parts: [{ type: 'text', text }] });
  }

  // 로그인 상태이고 메시지가 없으면 주제 선택 화면 표시
  const showTopicSelector = !!onSelectTopic && messages.length === 0;

  return (
    <div className='bg-background flex h-screen w-full'>
      <ThemeToggle />
      <div className='mx-auto flex w-full max-w-lg flex-col'>
        <ChatHeader
          isBusy={isBusy}
          messagesCount={messages.length}
          onStop={stop}
          onOpenDrawer={onOpenDrawer}
        />

        <ChatDescription />

        <div className='bg-card/80 flex min-h-0 flex-1 flex-col overflow-hidden shadow-2xl backdrop-blur-xl'>
          <div ref={scrollerRef} className='min-h-0 flex-1 overflow-y-auto px-4 py-4'>
            {showTopicSelector ? (
              <TopicSelector onSelect={onSelectTopic} isLoading={isTopicLoading} />
            ) : messages.length === 0 ? (
              <ChatErrorAlert error={error} />
            ) : (
              <div className='flex flex-col gap-4'>
                {messages.map((message) => (
                  <PreviewMessage key={message.id} message={message} isLoading={isBusy} />
                ))}

                {status === 'submitted' ? <TypingIndicator /> : null}

                <ChatErrorAlert error={error} />
              </div>
            )}
          </div>

          {!showTopicSelector && (
            <ChatInput value={input} onChange={setInput} onSend={onSend} isBusy={isBusy} />
          )}
        </div>
      </div>
    </div>
  );
}
